<!-- 
Search dialog.

All searchable listings will use the same search dialog, but it will display differently
depending on the searchFilters that are supplied:
- if the "show-all" prop is set then all search filters will be displayed at once
- otherwise, if there are no pinned search criteria then those search filters that are marked as default 
will be displayed and the others will be selectable from an "add more criteria" button
- if there are pinned search criteria then those will be displayed and the others will be selectable from
the "add more criteria" button
-->
<template>
  <v-dialog 
    v-if="show"
    :value="show" 
    persistent 
    content-class="dialog md" 
    @keydown.esc="hide"
  >
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="common" section="searching"/>
        </v-toolbar>
        <v-card-text :class="{'pb-0': !showAll}">
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <template v-for="(filter, index) in ourFilters">
                <v-flex :key="`filter-${index}`" :class="getWidth(filter)" xs11>
                  <v-autocomplete 
                    v-if="filter.type == filterTypeMultiselect"
                    :ref="`item-${index}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :items="getItems(filter)"
                    :label="filter.text"
                    class="mx-3"
                    multiple 
                    chips
                    deletable-chips
                    clearable
                  />

                  <v-autocomplete 
                    v-if="filter.type == filterTypeSelect"
                    :ref="`item-${index}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :items="getItems(filter)"
                    :label="filter.text"
                    :class="{fat: filter.fat}"
                    class="mx-3"
                    clearable
                  />

                  <v-select
                    v-if="filter.type == filterTypeYesNo"
                    :ref="`item-${index}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :items="lookups.yesNo"
                    :label="filter.text"
                    :class="{fat: filter.fat}"
                    class="mx-3"
                    clearable
                  />

                  <v-switch 
                    v-if="filter.type == filterTypeSwitch"
                    :ref="`item-${index}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :label="filter.text" 
                    color="primary"
                    class="mx-3"
                  />

                  <v-text-field 
                    v-if="filter.type == filterTypeText"
                    :ref="`item-${index}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :label="filter.text" 
                    :class="{fat: filter.fat}"
                    clearable 
                    class="mx-3"
                  />

                  <v-text-field 
                    v-if="filter.type == filterTypeInteger"
                    :ref="`filter-${filter.value}`"
                    v-model="ourCriteria[filter.value]"
                    :autofocus="autofocus(filter)"
                    :label="filter.text" 
                    :rules="[rules.integer, rules.greaterThanZero]"
                    :class="{fat: filter.fat}"
                    clearable 
                    class="mx-3"
                  />

                  <div v-if="filter.type == filterTypeDate" class="mx-3">
                    <our-date-picker
                      :ref="`filter-${filter.value}`"
                      v-model="ourCriteria[filter.value]"
                      :autofocus="autofocus(filter)"
                      :label="filter.text"
                    />
                  </div>

                  <div v-if="filter.type == filterTypeDateTime" class="mx-3">
                    <our-date-time-picker
                      :ref="`filter-${filter.value}`"
                      v-model="ourCriteria[filter.value]"
                      :autofocus="autofocus(filter)"
                      :label="filter.text"
                    />
                  </div>
                </v-flex>
                <v-flex v-if="!showAll" :key="`pin-filter-${index}`" xs1 align-self-center>
                  <v-tooltip bottom>
                    <v-btn 
                      slot="activator"
                      icon small
                      class="ma-0 primary--text" 
                      @click="togglePin(filter)"
                    >
                      <img 
                        :class="{'unselected-pin': !isPinned(filter), 'selected-pin': isPinned(filter)}" 
                        src="@/assets/pin.svg" 
                        alt="Pin or unpin this search criteria"
                      >
                    </v-btn>
                    <span v-if="isPinned(filter)">Unpin this criteria so it does not always appear</span>
                    <span v-else>Pin this criteria so it always appears</span>
                  </v-tooltip>
                </v-flex>
              </template>
              
              <v-flex v-if="canAddCriteria" xs12>
                <v-card :elevation="(adding ? 12 : 0)">
                  <v-card-text class="pb-0">
                    <v-layout wrap>
                      <v-flex shrink>
                        <v-btn depressed class="ml-0 mt-2 mb-4" @click="adding=!adding">
                          <v-icon class="mr-2">playlist_add</v-icon>
                          Add more criteria
                        </v-btn>
                      </v-flex>
                      <v-flex grow>
                        <v-autocomplete 
                          v-if="adding"
                          :items="filtersToAdd"
                          autofocus
                          label="Criteria"
                          class="add-more-criteria-field"
                          @input="addCriteria($event)"
                          @blur="adding=false"
                        />
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>

            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn flat color="error" @click="clearCriteria">
            Clear criteria
          </v-btn>
          <v-spacer/>
          <v-btn flat @click="hide">Cancel</v-btn>
          <v-btn 
            flat 
            color="primary" 
            type="submit" 
            @click.prevent="search"
          >
            Search
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" />
  </v-dialog>
</template>

<script>
import _ from "underscore";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import * as action from "@/store/searches/action-types";
import * as mutation from "@/store/searches/mutation-types";
import { deepCopy } from "@/store/helpers";
import { SAVED_SEARCHES_PRESELECTED_FILTER } from "@/store/searches/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import {
  FILTER_TYPE_TEXT,
  FILTER_TYPE_INTEGER,
  FILTER_TYPE_MULTISELECT,
  FILTER_TYPE_YESNO,
  FILTER_TYPE_SELECT,
  FILTER_TYPE_DATE,
  FILTER_TYPE_DATETIME,
  FILTER_TYPE_SWITCH
} from "@/store/system/helpers";

export default {
  mixins: [lookupMixins, ruleMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Array,
      required: true
    },
    criteria: {
      type: Object,
      required: true
    },
    criteriaMutation: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    showAll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      filterTypeText: FILTER_TYPE_TEXT,
      filterTypeInteger: FILTER_TYPE_INTEGER,
      filterTypeYesNo: FILTER_TYPE_YESNO,
      filterTypeSelect: FILTER_TYPE_SELECT,
      filterTypeMultiselect: FILTER_TYPE_MULTISELECT,
      filterTypeDate: FILTER_TYPE_DATE,
      filterTypeDateTime: FILTER_TYPE_DATETIME,
      filterTypeSwitch: FILTER_TYPE_SWITCH,
      ourCriteria: null,
      ourFilters: null,
      adding: false,
      lastAdded: null
    };
  },
  computed: {
    filtersToAdd() {
      const available = _(this.filters).filter(f => !this.hasFilter(f));
      return _(available).sortBy(f => (f.text.startsWith("(") ? "z" + f.text : f.text));
    },
    canAddCriteria() {
      return this.filtersToAdd.length > 0 && !this.showAll;
    },
    preselectedFilter() {
      return this.$store.state.searches.preselectedFilter;
    },
    pinnedCriteriaModule() {
      const m = this.criteriaMutation;
      if (m.startsWith("EXCEPTION_LOG")) return "exceptionLogs";
      if (m.startsWith("INVOICE")) return "invoices";
      if (m.startsWith("LEVY_COLLECTION_PROVIDER")) return "levyCollectionProviders";
      if (m.startsWith("NOTICE")) return "notices";
      if (m.startsWith("PRISMS_USER")) return "prismsUsers";
      if (m.startsWith("PROVIDER")) return "providers";
      if (m.startsWith("SYSTEM_LOG")) return "systemLogs";
      console.error(`Unrecognised criteria-mutation prefix '${this.criteriaMutation}'`);
      return null;
    },
    pinnedCriteria() {
      const pc = this.$store.state.searches.pinnedCriteria.data[this.pinnedCriteriaModule];
      return Array.isArray(pc) ? pc : [];
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.adding = false;
        this.lastAdded = null;
        this.ourCriteria = deepCopy(this.criteria);
        if (this.showAll) {
          this.ourFilters = deepCopy(this.filters);
        } else {
          const defaults = _(this.filters).filter(f => f.default);
          const selectedOrPinned = _(this.filters).filter(
            f => this.hasCriteria(f) || this.pinnedCriteria.includes(f.value)
          );
          this.ourFilters = selectedOrPinned.length > 0 ? deepCopy(selectedOrPinned) : deepCopy(defaults);
        }
      } else {
        this.$store.commit(SAVED_SEARCHES_PRESELECTED_FILTER, null);
      }
    },
    preselectedFilter() {
      if (this.preselectedFilter) {
        this.$emit("update:show", true);
      }
    }
  },
  mounted() {
    this.$store.dispatch(action.PINNED_CRITERIA_GET);
  },
  methods: {
    search() {
      if (this.$refs.form.validate()) {
        this.$store.commit(this.criteriaMutation, this.ourCriteria);
        this.$emit("click:search");
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    addCriteria(value) {
      const filter = _(this.filters).find(f => f.value == value);
      this.ourFilters.push(deepCopy(filter));
      this.adding = false;
      this.lastAdded = value;
    },
    clearCriteria() {
      for (var key in this.ourCriteria) {
        if (Array.isArray(this.ourCriteria[key])) {
          this.ourCriteria[key] = [];
        } else {
          this.ourCriteria[key] = null;
        }
      }
    },
    hasCriteria(filter) {
      const c = this.ourCriteria[filter.value];
      if (Array.isArray(c)) return c.length > 0;
      if (filter.type == FILTER_TYPE_SWITCH) return !!c;
      if (c === false) return true;
      return !!c;
    },
    isPinned(filter) {
      return this.pinnedCriteria.includes(filter.value);
    },
    togglePin(filter) {
      const data = { module: this.pinnedCriteriaModule, criteria: filter.value };
      if (this.isPinned(filter)) {
        this.$store.commit(mutation.PINNED_CRITERIA_REMOVE, data);
        this.$store.commit(SHOW_NOTIFICATION, `Criteria '${filter.text}' has been unpinned.`);
      } else {
        this.$store.commit(mutation.PINNED_CRITERIA_ADD, data);
        this.$store.commit(SHOW_NOTIFICATION, `Criteria '${filter.text}' has been pinned.`);
      }
      this.$store.dispatch(action.PINNED_CRITERIA_SAVE);
    },
    hasFilter(filter) {
      return _(this.ourFilters).some(f => f.value == filter.value);
    },
    hide() {
      this.$emit("update:show", false);
    },
    getItems(filter) {
      return filter.exclude ? _(filter.items).filter(i => !filter.exclude.includes(i.value)) : filter.items;
    },
    getWidth(filter) {
      return this.showAll && filter.width ? `sm${filter.width}` : null;
    },
    autofocus(filter) {
      return filter.value == this.lastAdded || (this.preselectedFilter && filter.value == this.preselectedFilter.value);
    }
  }
};
</script>

<style lang="scss">
div.fat {
  div.v-input__slot {
    height: 42px !important;
    padding-bottom: 9px !important;
  }
}
img.unselected-pin {
  // https://codepen.io/sosuke/pen/Pjoqqp
  filter: invert(100%) sepia(1%) saturate(7438%) hue-rotate(181deg) brightness(115%) contrast(63%);
  height: 17px;
}
img.selected-pin {
  height: 17px;
}
</style>